import type {
  DemoEnvironmentSettings,
  DemoEnvironmentStatus,
} from '../../services/api/types.gen';

/**
 * How each status reads in the console.
 *
 * The badge collapses two independent facts — freshness, and whether the last
 * run failed — because the operator only acts on the most urgent one. The
 * precedence itself is computed on the server, so this file only decides
 * wording. Whether the nightly refresh covers a sandbox is not part of the
 * badge; it is the sandbox's own setting, shown as its own toggle.
 */
export interface DemoEnvironmentStatusPresentation {
  label: string;
  className: string;
  hint: string;
}

/**
 * When the nightly refresh re-applies RogerDoc coding data and feature flags.
 * Must match the demo-state-refresh-daily schedule in the backend.
 */
export const DEMO_REFRESH_TIME_LABEL = '6:00 AM PT';

/**
 * The refresh toggle's tooltip. Only the demo stage has the schedule, so
 * elsewhere the toggle is disabled and says so rather than promising a refresh.
 *
 * The nightly job skips a sandbox that has never been set up, since there is
 * nothing to put back; the "on" hint says so rather than claiming a refresh.
 */
export function refreshToggleHint({
  scheduled,
  on,
  setUp,
}: {
  scheduled: boolean;
  on: boolean;
  setUp: boolean;
}): string {
  if (!scheduled)
    return 'The nightly refresh runs on the demo environment only.';
  if (!on) {
    return `Not refreshed nightly. Click to refresh it every night at ${DEMO_REFRESH_TIME_LABEL}.`;
  }
  if (!setUp) {
    return `Refreshed nightly at ${DEMO_REFRESH_TIME_LABEL} once it has been run. Click to turn off.`;
  }
  return `Refreshed nightly at ${DEMO_REFRESH_TIME_LABEL}. Click to turn off.`;
}

export const DEMO_ENVIRONMENT_STATUS_PRESENTATION: Record<
  DemoEnvironmentStatus,
  DemoEnvironmentStatusPresentation
> = {
  hidden: {
    label: 'Hidden',
    className: 'border-border bg-muted text-muted-foreground',
    hint: 'Hidden from My sandboxes. Unhide to bring it back — the settings are kept.',
  },
  running: {
    label: 'Running',
    className: 'border-blue-200 bg-blue-50 text-blue-800',
    hint: 'A run is in progress. Expand the row to follow its steps.',
  },
  failed: {
    label: 'Failed',
    className: 'border-red-200 bg-red-50 text-red-800',
    hint: 'The last run did not finish. Expand the row to see which step failed.',
  },
  never_run: {
    label: 'Never run',
    className: 'border-border bg-muted text-muted-foreground',
    hint: 'Not run from this console yet.',
  },
  stale: {
    label: 'Stale',
    className: 'border-amber-200 bg-amber-50 text-amber-800',
    hint: 'Nobody has touched this in weeks. Run it again to bring it up to date.',
  },
  idle: {
    label: 'Idle',
    className: 'border-blue-200 bg-blue-50 text-blue-800',
    hint: 'Set up recently, but nobody has signed in yet.',
  },
  active: {
    label: 'Active',
    className: 'border-green-200 bg-green-50 text-green-800',
    hint: 'Somebody signed in recently.',
  },
};

// What needs attention first. Hidden sorts last: it is retired on purpose, so it
// is the one row nobody has to do anything about.
const STATUS_ORDER: DemoEnvironmentStatus[] = [
  'running',
  'failed',
  'stale',
  'never_run',
  'idle',
  'active',
  'hidden',
];

export function compareByUrgency(
  a: DemoEnvironmentStatus,
  b: DemoEnvironmentStatus
): number {
  return STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b);
}

/**
 * Settings with every field present.
 *
 * The generated type makes each field optional because the backend model gives
 * them all defaults, so reading one straight off the response is
 * `T | undefined` everywhere. Resolving once here keeps that from spreading
 * `?? default` through the dialogs, where a missed one would silently save a
 * different setting than the operator saw.
 */
export type ResolvedDemoEnvironmentSettings = {
  include_coding: boolean;
  demo_ff_template_agency: string;
  demo_team_member_emails: string[];
  refresh_nightly: boolean;
};

export function resolveSettings(
  settings: DemoEnvironmentSettings | undefined,
  defaultTemplateAgency: string
): ResolvedDemoEnvironmentSettings {
  return {
    include_coding: settings?.include_coding ?? true,
    demo_ff_template_agency:
      settings?.demo_ff_template_agency ?? defaultTemplateAgency,
    demo_team_member_emails: settings?.demo_team_member_emails ?? [],
    refresh_nightly: settings?.refresh_nightly ?? true,
  };
}

/**
 * The system name, only when it adds something.
 *
 * For most agencies the written name and the system name are the same string,
 * so printing both shows the name twice. Returns null in that case — same rule
 * the retired setup form used for its agency labels.
 */
export function distinctSystemName(
  writtenName: string | null | undefined,
  systemName: string
): string | null {
  const written = (writtenName ?? '').trim();
  const system = systemName.trim();
  if (!written || written.toLowerCase() === system.toLowerCase()) {
    return null;
  }
  return system;
}
